import React, { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography, CircularProgress } from "@mui/material";
import { EditOutlined as EditIcon } from "@mui/icons-material";
import ReactPlayer from "react-player";
import { getThumbnailSrc, IMAGE_URL, onToast, ASSOCIATION_ACTION } from '../../../../core/Constant';
import { Actions } from "../../../../core/modules/Actions";
import TextButtonComponet from "../../../atom/Buttons/TextButton";
import PopUpMessageComponent from "../../PopupMessage/PopUpMessage";
import DocumentAssociationDialog from "../../Dialog/DocumentAssociationDialog";
import DialogComponent from "../../../../component/atom/Dialog/Dialog";
import defaultVideoThumbnail from "../../../../assets/Images/video_thumbnail.jpg";

const AdminVideoCard = ({
  title,
  id,
  description,
  btnText,
  videoUrl,
  thumbnail,
  subtitle_tracks = [],
  onVideoClick = () => { },
  onEdit,
  loadingEdit,
}) => {
  const videoThumbnail = getThumbnailSrc(thumbnail, "video_thumbnail") ?? defaultVideoThumbnail;
  const dispatch = useDispatch();
  const regeneratingVideoId = useSelector((state) => state.video.get("regeneratingVideoId"));
  const subtitleStatus = useSelector((state) => state.video.get("subtitleStatus"));
  const isRegenerating =
    regeneratingVideoId !== null &&
    regeneratingVideoId !== undefined &&
    (regeneratingVideoId === id || regeneratingVideoId == id);

  const regenerateProgressPercent = useMemo(() => {
    if (!isRegenerating || !subtitleStatus) return null;
    const videoId = subtitleStatus?.video_id || subtitleStatus?.data?.video_id;
    if (videoId !== undefined && videoId != id) return null;

    const completed = subtitleStatus?.completed_items ?? subtitleStatus?.data?.completed_items;
    const total = subtitleStatus?.total_items ?? subtitleStatus?.data?.total_items;

    if (total !== undefined && total > 0 && completed !== undefined) {
      return Math.round((completed / total) * 100);
    }
    return null;
  }, [isRegenerating, subtitleStatus, id]);

  const [deletePopup, setDeletePopup] = useState(false);
  const [regeneratePopup, setRegeneratePopup] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const videoAssociation = useSelector((state) =>
    state.video.get("videoAssociation")
  );

  const associateContent = (() => {
    const contents = videoAssociation?.associate_items?.courseContents;
    if (Array.isArray(contents)) return contents;
    if (contents && typeof contents === "object") return Object.values(contents);
    return [];
  })();

  // Memoize tracks array to stop infinite configuration updates
  const tracks = useMemo(() => {
    return (subtitle_tracks || []).map((track) => ({
      kind: track.kind || "subtitles",
      src: track.src,
      srcLang: track.srclang || track.srcLang || "en",
      label: track.label || "English",
      default: track.default || false,
    }));
  }, [subtitle_tracks]);

  const copyToClipboard = (link, textTitle) => {
    navigator.clipboard.writeText(link);
    const message = `${textTitle} URL copied successfully`;
    const responseJson = { status_code: "1", message };
    onToast("URL Copied", responseJson, false);
  };

  const deleteVideoBtn = async () => {
    setLoadingDelete(true);
    try {
      await dispatch(Actions.video.deleteAdminVideo(id));
      setDeletePopup(false);
    } catch (error) {
      console.error("Error deleting video:", error);
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleDeleteClick = async () => {
    setLoadingDelete(true);
    try {
      await dispatch(Actions.video.checkVideoAssociation(id));
      setDeletePopup(true);
    } catch (error) {
      console.error("Error checking video association:", error);
      setDeletePopup(true);
    } finally {
      setLoadingDelete(false);
    }
  };

  const regenerateSubtitlesBtn = () => {
    setRegeneratePopup(false);
    dispatch(Actions.video.regenerateSubtitles({ video_id: id }));
  };

  const handleEditClick = () => {
    if (loadingEdit) return;
    if (onEdit) {
      onEdit({
        id,
        video_id: id,
        title: description,
        description,
        thumbnail,
        videoUrl,
      });
    }
  };

  return (
    <>
      {/* 1. Main Card Box Container */}
      <Box
        mb={3}
        className="admin-video-card"
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: "12px",
          padding: "16px",
          boxSizing: "border-box"
        }}
      >
        {/* 2. Unified Responsive Video Box Wrapper */}
        <Box
          sx={{
            width: "100%",
            aspectRatio: "16/9", // Fix: Keeps the video element container fluid and safe across all grid sizing columns
            borderRadius: "10px",
            overflow: "hidden",
            backgroundColor: "#000000",
            position: "relative"
          }}
        >
          <ReactPlayer
            key={videoUrl}
            light={
              <img
                src={videoThumbnail}
                alt="Thumbnail"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            }
            width="100%"
            height="100%"
            url={videoUrl}
            playing={false}
            loop={true}
            controls={true}
            muted={true}
            config={{
              file: {
                attributes: {
                  controlsList: "nodownload",
                  crossOrigin: "anonymous",
                  style: { width: "100%", height: "100%", objectFit: "cover" }
                },
                tracks,
              },
            }}
          />
        </Box>

        {/* 3. Text Header Segment Layout */}
        {title && (
          <Typography
            mt={2}
            fontFamily="Montserrat"
            color="black"
            fontWeight={700}
            fontSize={16}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {title}
          </Typography>
        )}

        {/* Description Row Layout Block */}
        <Box sx={{ minHeight: 45, mt: 1.5, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }} >
          <Typography
            onClick={() => !regeneratingVideoId && !loadingEdit && handleEditClick()}
            title={description}
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              cursor: (regeneratingVideoId || loadingEdit) ? "not-allowed" : "pointer",
            }}
            fontFamily="Montserrat"
            color="#8c8c8c"
            fontWeight={500}
            fontSize={14}
            component="div"
          >
            {description}
          </Typography>
          {loadingEdit ? (
            <CircularProgress
              size={20}
              sx={{
                color: "#831DDC",
                flexShrink: 0,
                mt: 0.5,
              }}
            />
          ) : (
            <EditIcon
              onClick={() => !regeneratingVideoId && !loadingEdit && handleEditClick()}
              sx={{
                cursor: (regeneratingVideoId || loadingEdit) ? "not-allowed" : "pointer",
                color: "#64748B",
                "&:hover": { color: (regeneratingVideoId || loadingEdit) ? "#64748B" : "#831DDC" },
                flexShrink: 0,
                mt: 0.5,
              }}
            />
          )}
        </Box>

        {/* 4. Action Button Execution Columns */}
        <Box mt={2} sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
          <TextButtonComponet
            classStyle="btn btn-100 btn-copy-link"
            onButtonClick={() => copyToClipboard(videoUrl, description)}
            text={btnText}
            isDisabled={Boolean(regeneratingVideoId)}
          />
          <TextButtonComponet
            classStyle="btn btn-100 btn-video-delete"
            onButtonClick={handleDeleteClick}
            text={loadingDelete && !deletePopup ? <CircularProgress size={20} style={{ color: 'white' }} /> : "Delete"}
            isDisabled={Boolean(regeneratingVideoId) || loadingDelete}
          />
          <TextButtonComponet
            classStyle="btn btn-100 btn-copy-link"
            onButtonClick={() => !regeneratingVideoId && setRegeneratePopup(true)}
            isDisabled={Boolean(regeneratingVideoId)}
            text={
              isRegenerating ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                  <CircularProgress size={14} sx={{ color: "inherit" }} />
                  <span>
                    {regenerateProgressPercent !== null
                      ? `Regenerating ${regenerateProgressPercent}%`
                      : "Regenerating..."}
                  </span>
                </Box>
              ) : (
                "Regenerate Subtitles"
              )
            }
          />
        </Box>
      </Box>

      {/* PopUp Overlays */}
      {deletePopup && (associateContent.length > 0 ? (
        <DocumentAssociationDialog
          association={{
            data: videoAssociation?.associate_items,
            action: ASSOCIATION_ACTION.DELETE,
            entityType: "video",
            entityName: title || description,
          }}
          onConfirm={deleteVideoBtn}
          onClose={() => setDeletePopup(false)}
        />
      ) : (
        <PopUpMessageComponent
          open={deletePopup}
          type="other"
          title="Delete!"
          message={"Are you sure you want to delete this video?"}
          btntext={loadingDelete ? <CircularProgress size={20} style={{ color: 'white' }} /> : "Yes, delete"}
          onclick={deleteVideoBtn}
          altbtntext={loadingDelete ? null : "No"}
          altonclick={() => setDeletePopup(false)}
          onclose={() => setDeletePopup(false)}
        />
      ))}

      <PopUpMessageComponent
        open={regeneratePopup}
        type="other"
        title="Regenerate!"
        message="Are you sure you want to regenerate subtitle files? Note that regenerating subtitles will delete previously created subtitles to generate new files."
        btntext="Yes, regenerate"
        onclick={regenerateSubtitlesBtn}
        altbtntext="No"
        altonclick={() => setRegeneratePopup(false)}
        onclose={() => setRegeneratePopup(false)}
      />
    </>
  );
};

export default AdminVideoCard;
